/**
 * Device Configuration Tab Component
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
  Stack,
  IconButton,
  Tooltip,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Publish as DeployIcon,
  History as HistoryIcon,
  Code as CodeIcon,
  Settings as SettingsIcon,
  CloudUpload as CloudUploadIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

import { Device } from '../../types/Device';
import {
  DeviceConfig,
  DeviceConfigCreate,
  DeviceConfigUpdate,
  DeviceConfigHistory,
  DEFAULT_CONFIG,
  ConfigDeployResponse,
  DeploymentResult,
  hasInvalidCggtts,
} from '../../types/config';
import { configApi } from '../../services/configApi';
import ConfigForm from './ConfigForm';
import ConfigHistory from './ConfigHistory';
import ConfigYamlViewer from './ConfigYamlViewer';
import DeploymentStatus from './DeploymentStatus';
import SteeringModes from './SteeringModes';

interface ConfigTabProps {
  device: Device;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`config-tabpanel-${index}`}
      aria-labelledby={`config-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const ConfigTab: React.FC<ConfigTabProps> = ({ device }) => {
  // State management
  const [config, setConfig] = useState<DeviceConfig | null>(null);
  const [formData, setFormData] = useState<DeviceConfigCreate>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  
  // UI state
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showDeployDialog, setShowDeployDialog] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [currentDeployment, setCurrentDeployment] = useState<DeploymentResult | undefined>(undefined);

  // Load configuration on component mount
  useEffect(() => {
    loadConfig();
  }, [device.id]);

  // Track form changes
  useEffect(() => {
    if (config) {
      const isChanged = JSON.stringify(formData) !== JSON.stringify(configToFormData(config));
      setHasUnsavedChanges(isChanged);
    }
  }, [formData, config]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const deviceConfig = await configApi.getConfig(device.id);
      console.log('=== LOAD CONFIG DEBUG ===');
      console.log('Raw config from API:', deviceConfig);
      console.log('Steering mode from API:', deviceConfig.steering_mode);
      
      setConfig(deviceConfig);
      const formDataConverted = configToFormData(deviceConfig);
      console.log('Converted to formData:', formDataConverted);
      console.log('Steering mode in formData:', formDataConverted.steering_mode);
      console.log('==========================');
      
      setFormData(formDataConverted);
    } catch (error) {
      console.error('Failed to load configuration:', error);
      showErrorMessage('Failed to load device configuration');
      // Use default config if loading fails
      setFormData(DEFAULT_CONFIG);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // DEBUG: Log form data and changes
      console.log('=== SAVE DEBUG ===');
      console.log('Current formData:', formData);
      console.log('Original config:', config);
      
      // Validate configuration
      const validationErrors = configApi.validateConfig(formData);
      if (validationErrors.length > 0) {
        showErrorMessage(`Validation errors: ${validationErrors.join(', ')}`);
        return;
      }

      let savedConfig: DeviceConfig;
      if (config) {
        // Update existing configuration
        const updateData: DeviceConfigUpdate = getChangedFields(config, formData);
        console.log('Detected changes:', updateData);
        console.log('================');
        savedConfig = await configApi.updateConfig(device.id, updateData);
      } else {
        // Create new configuration
        console.log('Creating new config');
        console.log('================');
        savedConfig = await configApi.createConfig(device.id, formData);
      }

      setConfig(savedConfig);
      setFormData(configToFormData(savedConfig));
      setHasUnsavedChanges(false);
      showSuccessMessage('Configuration saved successfully');
      
    } catch (error: any) {
      console.error('Failed to save configuration:', error);
      showErrorMessage(
        error.response?.data?.detail || 'Failed to save configuration'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeploy = async () => {
    try {
      setDeploying(true);
      const response: ConfigDeployResponse = await configApi.deployConfig(device.id);
      
      // Create deployment result for tracking
      const deployment: DeploymentResult = {
        deployment_id: response.deployment_id,
        device_id: device.id,
        config_version: response.config_version,
        status: response.status as any,
        timestamp: response.timestamp,
        message: response.message,
        mqtt_topic: response.mqtt_topic,
        attempts: 1
      };
      
      setCurrentDeployment(deployment);
      showSuccessMessage(
        `Configuration v${response.config_version} deployment initiated (ID: ${response.deployment_id.slice(-8)})`
      );
      setShowDeployDialog(false);
      
      // Switch to deployment tab to show progress
      setTabIndex(4);
      
    } catch (error: any) {
      console.error('Failed to deploy configuration:', error);
      showErrorMessage(
        error.response?.data?.detail || 'Failed to deploy configuration'
      );
    } finally {
      setDeploying(false);
    }
  };

  const handleRefresh = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to refresh?'
      );
      if (!confirmed) return;
    }
    loadConfig();
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  // Utility functions
  const configToFormData = (config: DeviceConfig): DeviceConfigCreate => ({
    ocxo_control_type: config.ocxo_control_type || 'SPI',
    pps_offset_ns: (config as any).pps_offset_ns ?? 0,
    ocxo_control_baseline: (config as any).ocxo_control_baseline ?? 500000,
    steering_mode: config.steering_mode || 'single_channel_gnss',
    // is_adsb_enabled is automatically calculated on the backend based on steering_mode
    num_slots: config.num_slots,
    level: (config as any).level ?? 2,
    // Configs saved before the levels have no exclusions: start empty
    adsb_excluded_master_ids: config.adsb_excluded_master_ids ?? [],
    latitude_gnss_ant: (config as any).latitude_gnss_ant ?? 44.42034947342054,
    longitude_gnss_ant: (config as any).longitude_gnss_ant ?? 7.547929586505649,
    height_gnss_ant_m: (config as any).height_gnss_ant_m ?? 0.0,
    latitude_adsb_ant: (config as any).latitude_adsb_ant ?? 0.0,
    longitude_adsb_ant: (config as any).longitude_adsb_ant ?? 0.0,
    height_adsb_ant_m: (config as any).height_adsb_ant_m ?? 0.0,
    openloop_command: (config as any).openloop_command ?? 555500,
    gnss_count: config.gnss_count,
    gnss_internal_delays: config.gnss_internal_delays,
    gnss_cable_delays: config.gnss_cable_delays,
    adsb_internal_delay: (config as any).adsb_internal_delay ?? 0.0,
    adsb_cable_delay: (config as any).adsb_cable_delay ?? 0.0,
    ext_ref_internal_delay: (config as any).ext_ref_internal_delay ?? 0.0,
    ext_ref_cable_delay: (config as any).ext_ref_cable_delay ?? 0.0,
    kalman_cggtts_mode: (config as any).kalman_cggtts_mode ?? false,
    cggtts_ref_dly: (config as any).cggtts_ref_dly ?? 0.0,
    cggtts_cab_dly: (config as any).cggtts_cab_dly ?? Array.from({ length: config.gnss_count }, () => 0),
    cggtts_int_dly: (config as any).cggtts_int_dly ?? Array.from({ length: config.gnss_count }, () => [0, 0]),
    cggtts_cal_id: (config as any).cggtts_cal_id ?? 'NA',
    controller_type: config.controller_type,
    pi_kp: config.pi_kp,
    pi_ki: config.pi_ki,
    kalman_window_size: config.kalman_window_size,
    pi_tau_n_minutes: (config as any).pi_tau_n_minutes ?? 1.0,
    pi_zeta: (config as any).pi_zeta ?? 1.0,
    pi_tau3_ratio: (config as any).pi_tau3_ratio ?? 1 / 6,
    pi_fo_hz: (config as any).pi_fo_hz ?? 1e7,
    pi_y_step_hz: (config as any).pi_y_step_hz ?? 6.6736e-6,
    notes: config.notes || '',
  });

  const getChangedFields = (
    original: DeviceConfig,
    updated: DeviceConfigCreate
  ): DeviceConfigUpdate => {
    const changes: DeviceConfigUpdate = {};
    
    // List all possible fields to check
    // Note: is_adsb_enabled is automatically calculated and not user-configurable
    const fieldsToCheck: (keyof DeviceConfigCreate)[] = [
      'ocxo_control_type', 'pps_offset_ns', 'ocxo_control_baseline', 'steering_mode', 'num_slots', 'level',
      'adsb_excluded_master_ids',
      'latitude_gnss_ant', 'longitude_gnss_ant', 'height_gnss_ant_m',
      'latitude_adsb_ant', 'longitude_adsb_ant', 'height_adsb_ant_m', 'openloop_command',
      'gnss_count', 'gnss_internal_delays', 'gnss_cable_delays', 'adsb_internal_delay', 'adsb_cable_delay', 'ext_ref_internal_delay', 'ext_ref_cable_delay',
      'kalman_cggtts_mode', 'cggtts_ref_dly', 'cggtts_cab_dly', 'cggtts_int_dly', 'cggtts_cal_id',
      'controller_type',
      'pi_kp', 'pi_ki', 'kalman_window_size',
      'pi_tau_n_minutes', 'pi_zeta', 'pi_tau3_ratio', 'pi_fo_hz', 'pi_y_step_hz',
      'notes'
    ];
    
    fieldsToCheck.forEach((key) => {
      // Fields absent in older configs compare as their form default
      const originalValue = key === 'adsb_excluded_master_ids' ? original[key] ?? [] : original[key];
      const updatedValue = updated[key];
      
      // Handle array comparisons properly
      if (Array.isArray(originalValue) && Array.isArray(updatedValue)) {
        // JSON comparison also covers nested arrays (cggtts_int_dly pairs)
        if (JSON.stringify(originalValue) !== JSON.stringify(updatedValue)) {
          (changes as any)[key] = updatedValue;
        }
      } 
      // Handle primitive values
      else if (originalValue !== updatedValue) {
        (changes as any)[key] = updatedValue;
      }
    });
    
    // Debug log
    console.log('Field comparison details:');
    fieldsToCheck.forEach((key) => {
      console.log(`${key}: ${original[key]} -> ${updated[key]} (changed: ${original[key] !== updated[key]})`);
    });
    
    return changes;
  };

  const showSuccessMessage = (message: string) => {
    setSuccessMessage(message);
    setShowSuccess(true);
  };

  // FastAPI 422 `detail` is an array of objects: always store a string,
  // since React cannot render objects as children.
  const showErrorMessage = (message: unknown) => {
    let text: string;
    if (Array.isArray(message)) {
      text = message
        .map((e: any) =>
          e && typeof e === 'object' && 'msg' in e
            ? `${Array.isArray(e.loc) && e.loc.length ? e.loc[e.loc.length - 1] : 'error'}: ${e.msg}`
            : typeof e === 'object' ? JSON.stringify(e) : String(e)
        )
        .join('; ');
    } else if (message && typeof message === 'object') {
      text = JSON.stringify(message);
    } else {
      text = String(message);
    }
    setErrorMessage(text);
    setShowError(true);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          border: '1px solid rgba(255, 255, 255, 0.08)',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Device Configuration
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              {config && (
                <>
                  <Chip
                    label={`Version ${config.version}`}
                    color="primary"
                    size="small"
                  />
                  <Typography variant="body2" color="text.secondary">
                    Last updated: {format(new Date(config.updated_at), 'MMM d, yyyy HH:mm')}
                  </Typography>
                </>
              )}
              {hasUnsavedChanges && (
                <Chip
                  label="Unsaved changes"
                  color="warning"
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>
          </Box>

          <Stack direction="row" spacing={2}>
            <Tooltip title="Refresh configuration">
              <IconButton onClick={handleRefresh} disabled={saving}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            
            <Button
              variant="outlined"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving || !hasUnsavedChanges || hasInvalidCggtts(formData)}
              loading={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>

            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={() => setShowDeployDialog(true)}
              disabled={!config || hasUnsavedChanges || deploying}
              color="secondary"
            >
              Deploy
            </Button>
          </Stack>
        </Box>
      </Paper>

      {config?.preset_errors && config.preset_errors.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          This configuration is not valid anymore: {config.preset_errors.join('; ')}
        </Alert>
      )}

      {/* Sub-tabs */}
      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          icon={<TuneIcon />}
          iconPosition="start"
          label="Steering Modes"
          id="config-tab-0"
        />
        <Tab
          icon={<SettingsIcon />}
          iconPosition="start"
          label="Configuration"
          id="config-tab-1"
        />
        <Tab
          icon={<HistoryIcon />}
          iconPosition="start"
          label="History"
          id="config-tab-2"
        />
        <Tab
          icon={<CodeIcon />}
          iconPosition="start"
          label="YAML View"
          id="config-tab-3"
        />
        <Tab
          icon={<CloudUploadIcon />}
          iconPosition="start"
          label="Deployment"
          id="config-tab-4"
        />
      </Tabs>

      {/* Tab Panels */}
      <TabPanel value={tabIndex} index={0}>
        <SteeringModes
          formData={formData}
          onChange={setFormData}
          disabled={saving}
        />
      </TabPanel>

      <TabPanel value={tabIndex} index={1}>
        <ConfigForm
          deviceId={device.id}
          formData={formData}
          onChange={setFormData}
          disabled={saving}
        />
      </TabPanel>

      <TabPanel value={tabIndex} index={2}>
        <ConfigHistory deviceId={device.id} />
      </TabPanel>

      <TabPanel value={tabIndex} index={3}>
        <ConfigYamlViewer deviceId={device.id} />
      </TabPanel>

      <TabPanel value={tabIndex} index={4}>
        <DeploymentStatus 
          deviceId={device.id}
          currentDeployment={currentDeployment}
          onDeploymentUpdate={setCurrentDeployment}
        />
      </TabPanel>

      {/* Deploy Confirmation Dialog */}
      <Dialog open={showDeployDialog} onClose={() => setShowDeployDialog(false)}>
        <DialogTitle>Deploy Configuration</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to deploy the current configuration to the device?
            This will send the configuration via MQTT and restart the device services.
          </Typography>
          {config && (
            <Box mt={2}>
              <Typography variant="subtitle2" color="text.secondary">
                Configuration Version: {config.version}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Last Updated: {format(new Date(config.updated_at), 'MMM d, yyyy HH:mm:ss')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeployDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDeploy}
            variant="contained"
            color="secondary"
            disabled={deploying}
            startIcon={deploying ? <CircularProgress size={16} /> : <DeployIcon />}
          >
            {deploying ? 'Deploying...' : 'Deploy'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert severity="success" onClose={() => setShowSuccess(false)}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={showError}
        autoHideDuration={8000}
        onClose={() => setShowError(false)}
      >
        <Alert severity="error" onClose={() => setShowError(false)}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConfigTab; 